import React, { useState, useEffect } from 'react';
import { Search, HelpCircle, ThumbsUp, ThumbsDown, BookOpen, MessageCircle, X, Filter, SlidersHorizontal } from 'lucide-react';
import faqAPI, { FAQ, FAQCategory } from '../services/faqAPI';

const FAQHelp: React.FC = () => {
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatResponse, setChatResponse] = useState<any>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'most_viewed' | 'most_helpful'>('most_viewed');
  const [minViews, setMinViews] = useState<number>(0);
  const [minHelpful, setMinHelpful] = useState<number>(0);

  useEffect(() => {
    loadCategories();
    loadFAQs();
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [faqs, sortBy, minViews, minHelpful]);

  const loadCategories = async () => {
    try {
      const data = await faqAPI.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadFAQs = async () => {
    try {
      setLoading(true);
      const response = await faqAPI.getFAQs({ limit: 50, is_published: true });
      setFaqs(response.faqs);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      try {
        setLoading(true);
        const response = await faqAPI.getFAQs({
          limit: 50,
          category_id: selectedCategory || undefined,
          is_published: true
        });
        setFaqs(response.faqs);
      } catch (error) {
        console.error('Failed to load FAQs:', error);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const results = await faqAPI.searchFAQs(searchQuery, selectedCategory || undefined);
      setFaqs(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...faqs];

    filtered = filtered.filter(faq => {
      if (minViews > 0 && faq.view_count < minViews) return false;
      if (minHelpful > 0 && faq.helpful_count < minHelpful) return false;
      return true;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'most_viewed':
          return b.view_count - a.view_count;
        case 'most_helpful':
          return b.helpful_count - a.helpful_count;
        default:
          return 0;
      }
    });

    setFilteredFaqs(filtered);
  };

  const handleCategoryFilter = async (categoryId: number | null) => {
    setSelectedCategory(categoryId);
  };

  const resetFilters = () => {
    setSortBy('most_viewed');
    setMinViews(0);
    setMinHelpful(0);
    setSearchQuery('');
    setSelectedCategory(null);
  };

  const handleFAQClick = async (faqId: number) => {
    const isExpanding = expandedFAQ !== faqId;
    setExpandedFAQ(isExpanding ? faqId : null);

    if (isExpanding) {
      try {
        await faqAPI.trackView(faqId);
        // Only update filteredFaqs to prevent re-sorting. Don't update faqs state.
        const updatedFilteredFaqs = filteredFaqs.map(faq =>
          faq.id === faqId
            ? { ...faq, view_count: faq.view_count + 1 }
            : faq
        );
        setFilteredFaqs(updatedFilteredFaqs);
      } catch (error) {
        console.error('Failed to track view:', error);
      }
    }
  };

  const handleMarkHelpful = async (faqId: number, helpful: boolean) => {
    try {
      const result = await faqAPI.markHelpful(faqId, helpful);

      // Only update filteredFaqs to prevent re-sorting. Don't update faqs state.
      if (result.changed) {
        const updatedFilteredFaqs = filteredFaqs.map(faq => {
          if (faq.id === faqId) {
            return {
              ...faq,
              helpful_count: helpful ? faq.helpful_count + 1 : faq.helpful_count - 1,
              not_helpful_count: !helpful ? faq.not_helpful_count + 1 : faq.not_helpful_count - 1
            };
          }
          return faq;
        });
        setFilteredFaqs(updatedFilteredFaqs);
      } else {
        const updatedFilteredFaqs = filteredFaqs.map(faq =>
          faq.id === faqId
            ? {
                ...faq,
                helpful_count: helpful ? faq.helpful_count + 1 : faq.helpful_count,
                not_helpful_count: !helpful ? faq.not_helpful_count + 1 : faq.not_helpful_count
              }
            : faq
        );
        setFilteredFaqs(updatedFilteredFaqs);
      }
    } catch (error: any) {
      console.error('Failed to mark feedback:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      }
    }
  };

  const handleAskAI = async () => {
    if (!chatQuestion.trim()) return;

    try {
      setChatLoading(true);
      const response = await faqAPI.askFAQChat(chatQuestion);
      setChatResponse(response);
    } catch (error) {
      console.error('AI chat failed:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const getCategoryIcon = (iconName?: string) => {
    return <BookOpen className="w-5 h-5" />;
  };

  const formatMarkdownResponse = (text: string) => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    lines.forEach((line, index) => {
      if (line.trim() === '') {
        elements.push(<div key={`space-${key++}`} className="h-2"></div>);
        return;
      }

      if (line.startsWith('###')) {
        const content = line.replace(/^###\s*/, '').trim();
        elements.push(
          <h3 key={`h3-${key++}`} className="text-lg font-bold text-super-dark-purple mt-4 mb-2">
            {formatInlineMarkdown(content)}
          </h3>
        );
      } else if (line.startsWith('##')) {
        const content = line.replace(/^##\s*/, '').trim();
        elements.push(
          <h2 key={`h2-${key++}`} className="text-xl font-bold text-dusky-purple mt-4 mb-3">
            {formatInlineMarkdown(content)}
          </h2>
        );
      } else if (line.startsWith('#')) {
        const content = line.replace(/^#\s*/, '').trim();
        elements.push(
          <h1 key={`h1-${key++}`} className="text-2xl font-bold text-super-dark-purple mt-4 mb-3">
            {formatInlineMarkdown(content)}
          </h1>
        );
      } else if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        const content = line.replace(/^\s*[-•]\s*/, '').trim();
        elements.push(
          <div key={`li-${key++}`} className="flex items-start gap-2 ml-4 mb-2">
            <span className="text-dusky-purple mt-1">•</span>
            <span className="text-gray-700 flex-1">{formatInlineMarkdown(content)}</span>
          </div>
        );
      } else if (line.trim().match(/^\d+\./)) {
        const content = line.replace(/^\s*\d+\.\s*/, '').trim();
        const number = line.match(/^\s*(\d+)\./)?.[1];
        elements.push(
          <div key={`ol-${key++}`} className="flex items-start gap-2 ml-4 mb-2">
            <span className="text-dusky-purple font-semibold mt-0.5">{number}.</span>
            <span className="text-gray-700 flex-1">{formatInlineMarkdown(content)}</span>
          </div>
        );
      } else {
        elements.push(
          <p key={`p-${key++}`} className="text-gray-700 mb-2 leading-relaxed">
            {formatInlineMarkdown(line)}
          </p>
        );
      }
    });

    return elements;
  };

  const formatInlineMarkdown = (text: string): (string | JSX.Element)[] => {
    const parts: (string | JSX.Element)[] = [];
    let currentText = text;
    let key = 0;

    const boldRegex = /\*\*(.+?)\*\*/g;
    const italicRegex = /\*(.+?)\*/g;
    const codeRegex = /`(.+?)`/g;

    let lastIndex = 0;
    let match;

    const combinedRegex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;

    while ((match = combinedRegex.exec(currentText)) !== null) {
      if (match.index > lastIndex) {
        parts.push(currentText.substring(lastIndex, match.index));
      }

      if (match[1]) {
        parts.push(
          <strong key={`bold-${key++}`} className="font-bold text-super-dark-purple">
            {match[2]}
          </strong>
        );
      } else if (match[3] && !match[0].startsWith('**')) {
        parts.push(
          <em key={`italic-${key++}`} className="italic text-dusky-purple">
            {match[4]}
          </em>
        );
      } else if (match[5]) {
        parts.push(
          <code key={`code-${key++}`} className="px-2 py-0.5 bg-gray-100 text-dusky-purple rounded text-sm font-mono">
            {match[6]}
          </code>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < currentText.length) {
      parts.push(currentText.substring(lastIndex));
    }

    return parts.length > 0 ? parts : [text];
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">FAQ Help Center</h1>
          <p className="text-gray-600">Find answers to common questions about JARVIS RCSA</p>
        </div>
        <button
          onClick={() => setShowChat(!showChat)}
          className="bg-dusky-purple text-white px-4 py-2 rounded-lg hover:bg-dusky-purple transition-colors flex items-center space-x-2"
        >
          <MessageCircle className="w-4 h-4" />
          <span>Summarize FAQs</span>
        </button>
      </div>

      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dusky-purple w-5 h-5" />
          <input
            type="text"
            placeholder="Start typing to search FAQs... (searches as you type)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-super-dark-purple focus:border-transparent"
          />
          {loading && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-dusky-purple"></div>
            </div>
          )}
        </div>
        {searchQuery && (
          <div className="mt-2 text-sm text-gray-600">
            {faqs.length > 0 ? (
              <span>Found {faqs.length} result{faqs.length !== 1 ? 's' : ''}</span>
            ) : (
              <span className="text-red-600">No results found. Try different keywords.</span>
            )}
          </div>
        )}
      </div>

      {showChat && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-super-dark-purple flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-dusky-purple" />
              Ask Jarvis
            </h2>
            <button onClick={() => setShowChat(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <textarea
                value={chatQuestion}
                onChange={(e) => setChatQuestion(e.target.value)}
                placeholder="Ask any question about JARVIS RCSA..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                rows={3}
              />
            </div>
            <button
              onClick={handleAskAI}
              disabled={chatLoading || !chatQuestion.trim()}
              className="px-6 py-3 bg-dusky-purple text-white rounded-lg font-semibold hover:bg-dusky-purple transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {chatLoading ? 'Getting answer...' : 'Get Answer'}
            </button>

            {chatResponse && (
              <div className="mt-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border-2 border-dusky-purple/20 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-dusky-purple to-super-dark-purple px-6 py-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <MessageCircle className="w-6 h-6" />
                    Jarvis Answer
                  </h3>
                </div>

                <div className="p-6">
                  <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    {formatMarkdownResponse(chatResponse.answer)}
                  </div>

                  {chatResponse.suggestedFAQs && chatResponse.suggestedFAQs.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="h-1 w-1 bg-dusky-purple rounded-full"></div>
                        <h4 className="font-bold text-super-dark-purple text-lg">Related FAQs</h4>
                        <div className="h-1 flex-1 bg-gradient-to-r from-dusky-purple/30 to-transparent"></div>
                      </div>
                      <div className="space-y-3">
                        {chatResponse.suggestedFAQs.map((faq: any, index: number) => (
                          <div
                            key={`${faq.id}-${index}`}
                            className="bg-white rounded-lg border-2 border-gray-200 hover:border-dusky-purple hover:shadow-md transition-all overflow-hidden"
                          >
                            <button
                              onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                              className="w-full p-4 text-left group"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="font-semibold text-dusky-purple group-hover:text-super-dark-purple transition-colors">
                                    {faq.question}
                                  </div>
                                  {faq.similarity_score && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Relevance: {(faq.similarity_score * 100).toFixed(0)}%
                                    </div>
                                  )}
                                </div>
                                <div className={`transform transition-transform ${expandedFAQ === faq.id ? 'rotate-180' : ''}`}>
                                  <svg className="w-5 h-5 text-dusky-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                            </button>

                            {expandedFAQ === faq.id && (
                              <div className="px-4 pb-4 border-t border-gray-200">
                                <div className="pt-4 text-gray-700 leading-relaxed">
                                  {faq.answer}
                                </div>
                                {faq.source_reference && (
                                  <div className="text-sm text-gray-500 mt-3 flex items-center gap-1">
                                    <BookOpen className="w-3 h-3" />
                                    <span>Source: {faq.source_reference}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-6">
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-super-dark-purple mb-4">Categories</h2>
            <div className="space-y-2">
              <button
                onClick={() => handleCategoryFilter(null)}
                className={`w-full text-left px-4 py-3 rounded-lg transition ${
                  selectedCategory === null
                    ? 'bg-dusky-purple text-white font-semibold'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                All Categories
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryFilter(category.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition flex items-center gap-3 ${
                    selectedCategory === category.id
                      ? 'bg-dusky-purple text-white font-semibold'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {getCategoryIcon(category.icon)}
                  <span className="flex-1">{category.name}</span>
                  {category.faq_count !== undefined && (
                    <span className="text-sm opacity-75">({category.faq_count})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-super-dark-purple flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filter FAQs
              </h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-dusky-purple hover:text-super-dark-purple transition flex items-center gap-2"
              >
                <SlidersHorizontal className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>

            {showFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as any)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                    >
                      <option value="most_viewed">Most Viewed</option>
                      <option value="most_helpful">Most Helpful</option>
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Views
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={minViews}
                      onChange={(e) => setMinViews(parseInt(e.target.value) || 0)}
                      placeholder="e.g. 10"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Helpful Votes
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={minHelpful}
                      onChange={(e) => setMinHelpful(parseInt(e.target.value) || 0)}
                      placeholder="e.g. 5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dusky-purple focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {filteredFaqs.length} of {faqs.length} FAQs
                  </div>
                  <button
                    onClick={resetFilters}
                    className="px-4 py-2 text-dusky-purple hover:bg-gray-100 rounded-lg transition font-medium"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-dusky-purple"></div>
              <p className="mt-4 text-gray-600">Loading FAQs...</p>
            </div>
          ) : filteredFaqs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-super-dark-purple mb-2">No FAQs found</h3>
              <p className="text-gray-600">
                {faqs.length > 0 ? 'Try adjusting your filters or search criteria.' : 'Try adjusting your search or browse all categories.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq) => (
                <div key={faq.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition">
                  <button
                    onClick={() => handleFAQClick(faq.id)}
                    className="w-full text-left p-6 flex items-start gap-4"
                  >
                    <HelpCircle className="w-6 h-6 text-dusky-purple flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-lg font-semibold text-super-dark-purple">{faq.question}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{faq.view_count} views</span>
                        </div>
                      </div>
                      {faq.category && (
                        <span className="inline-block mt-2 px-3 py-1 bg-dusky-purple/10 text-dusky-purple text-sm rounded-full">
                          {faq.category.name}
                        </span>
                      )}
                    </div>
                  </button>

                  {expandedFAQ === faq.id && (
                    <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                      <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">{faq.answer}</div>

                      {faq.source_reference && (
                        <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                          <strong>Source:</strong> {faq.source_reference}
                        </div>
                      )}

                      {faq.tags && faq.tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {faq.tags.map((tag, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-6 flex items-center gap-4 pt-4 border-t border-gray-200">
                        <span className="text-sm text-gray-600">Was this helpful?</span>
                        <button
                          onClick={() => handleMarkHelpful(faq.id, true)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 rounded transition"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          Yes ({faq.helpful_count})
                        </button>
                        <button
                          onClick={() => handleMarkHelpful(faq.id, false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded transition"
                        >
                          <ThumbsDown className="w-4 h-4" />
                          No ({faq.not_helpful_count})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQHelp;
